import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import siteConfig from '../data/siteConfig.json';
import SafeIcon from '../common/SafeIcon';
import { FiInstagram, FiX } from 'react-icons/fi';

const galleryProjects = siteConfig.galleryProjects;

const BeforeAfterImage = ({ project, position, setPosition, beforeLabel, afterLabel, large = false, interactive = true }) => (
  <div className={`relative overflow-hidden bg-slate-900 ${large ? 'h-[60vh] min-h-[360px]' : 'aspect-[4/3]'}`}>
    <img src={project.after} alt={afterLabel} className="absolute inset-0 w-full h-full object-cover" />
    <div className="absolute inset-0 overflow-hidden" style={{ width: `${position}%` }}>
      <img src={project.before} alt={beforeLabel} className="w-full h-full object-cover max-w-none" style={{ width: `${10000 / position}%` }} />
    </div>
    <div className="absolute left-4 top-4 bg-slate-950/75 text-white px-3 py-2 text-xs font-bold uppercase tracking-[0.25em]">
      {beforeLabel}
    </div>
    <div className="absolute right-4 top-4 bg-slate-950/75 text-white px-3 py-2 text-xs font-bold uppercase tracking-[0.25em]">
      {afterLabel}
    </div>
    <div className="absolute inset-y-0 flex items-center" style={{ left: `${position}%` }}>
      <div className="h-full w-px bg-white/80" />
      <div className="-ml-6 w-12 h-12 rounded-full bg-slate-950 text-white shadow-xl flex items-center justify-center">
        <span className="text-xl leading-none">&#8596;</span>
      </div>
    </div>
    {interactive && (
      <input
        aria-label={`${beforeLabel} / ${afterLabel}`}
        type="range"
        min="18"
        max="82"
        value={position}
        onChange={(event) => setPosition(Number(event.target.value))}
        className="absolute inset-0 w-full h-full opacity-0 cursor-ew-resize"
      />
    )}
  </div>
);

const Gallery = () => {
  const { t } = useLanguage();
  const [selectedProject, setSelectedProject] = useState(null);
  const [sliderPosition, setSliderPosition] = useState(50);
  const cases = t('galleryPage.cases');

  const openProject = (index) => {
    setSelectedProject(index);
    setSliderPosition(50);
  };

  const closeProject = () => setSelectedProject(null);
  const fallbackCase = cases[0];
  const activeCase = selectedProject !== null ? (cases[selectedProject] || fallbackCase) : null;
  const activeProject = selectedProject !== null ? galleryProjects[selectedProject] : null;

  return (
    <div className="bg-slate-50 min-h-screen py-16 md:py-20 pb-24">
      <div className="container mx-auto px-6 max-w-7xl">
        <div className="text-center mb-12 md:mb-16">
          <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">{t('galleryPage.title')}</h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">{t('galleryPage.subtitle')}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {galleryProjects.map((project, index) => (
            <motion.button
              key={`${project.before}-${index}`}
              type="button"
              onClick={() => openProject(index)}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.08 }}
              className="group text-left bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl border border-slate-100 transition-all"
            >
              <BeforeAfterImage
                project={project}
                position={50}
                setPosition={() => {}}
                beforeLabel={t('galleryPage.before')}
                afterLabel={t('galleryPage.after')}
                interactive={false}
              />
              <div className="p-6">
                <h2 className="text-xl font-bold text-slate-900 mb-3">{(cases[index] || fallbackCase).title}</h2>
                <p className="text-sm text-slate-500 mb-4">{(cases[index] || fallbackCase).location}</p>
                <div className="flex flex-wrap gap-2">
                  {(cases[index] || fallbackCase).tags.map((tag) => (
                    <span key={tag} className="px-3 py-1 rounded-full bg-slate-50 text-xs font-bold text-slate-600">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </motion.button>
          ))}
        </div>

        <div className="mt-12 text-center">
          <a
            href={siteConfig.links.instagram}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center justify-center gap-2 bg-slate-900 hover:bg-glass-blue text-white px-8 py-4 rounded-lg font-bold transition-colors shadow-lg"
          >
            <SafeIcon icon={FiInstagram} />
            {t('common.viewMore')}
          </a>
        </div>
      </div>

      <AnimatePresence>
        {activeProject && activeCase && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[80] bg-slate-950/80 p-4 md:p-8 flex items-center justify-center"
            onClick={closeProject}
          >
            <motion.div
              initial={{ opacity: 0, y: 24, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 24, scale: 0.98 }}
              className="bg-white w-full max-w-6xl max-h-[90vh] overflow-y-auto rounded-2xl shadow-2xl"
              onClick={(event) => event.stopPropagation()}
            >
              <div className="grid lg:grid-cols-[1.1fr_0.9fr]">
                <BeforeAfterImage
                  project={activeProject}
                  position={sliderPosition}
                  setPosition={setSliderPosition}
                  beforeLabel={t('galleryPage.before')}
                  afterLabel={t('galleryPage.after')}
                  large
                />
                <div className="p-6 md:p-10 flex flex-col">
                  <div className="flex items-start justify-between gap-4 mb-8">
                    <div>
                      <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-3">{activeCase.title}</h2>
                      <p className="text-slate-500 text-lg">{activeCase.location}</p>
                    </div>
                    <button
                      type="button"
                      onClick={closeProject}
                      className="w-11 h-11 rounded-full bg-slate-900 text-white flex items-center justify-center flex-shrink-0"
                      aria-label={t('galleryPage.close')}
                    >
                      <SafeIcon icon={FiX} className="text-xl" />
                    </button>
                  </div>
                  <p className="text-lg leading-relaxed text-slate-700 mb-8">{activeCase.desc}</p>
                  <div className="flex flex-wrap gap-3 mt-auto">
                    {activeCase.tags.map((tag) => (
                      <span key={tag} className="px-4 py-3 border border-slate-200 text-slate-600 text-sm font-bold uppercase tracking-widest">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Gallery;
